/**
 * Youth Crews — Testimonials carousel
 *
 * Upgrades the graceful, no-JS scroll-snap review row (see yc-testimonials.liquid)
 * into a looping, auto-rotating slider. Ported from the prototype v3.js
 * `initTestimonials`, but scoped per section instance so multiple bands (and the
 * theme editor's live re-render) work independently.
 *
 * Behavior:
 *  - Clones the first/last card so stepping off either end wraps seamlessly.
 *  - Prev/next arrows, generated dots, pointer drag/swipe (horizontal-only).
 *  - Auto-rotate, paused on hover / tab-hidden / prefers-reduced-motion.
 *  - Keyboard accessible (arrows and dots are real <button>s).
 */
(function () {
  'use strict';

  function initSlider(slider) {
    if (!slider || slider.dataset.ycInit === 'true') return;

    var track = slider.querySelector('[data-yct-track]');
    var dotsWrap = slider.querySelector('[data-yct-dots]');
    if (!track) return;

    var real = Array.prototype.slice.call(track.children);
    if (real.length < 2) {
      // Nothing to rotate — leave the single card as static content.
      slider.dataset.ycInit = 'true';
      slider.classList.add('is-loaded');
      return;
    }

    slider.dataset.ycInit = 'true';
    slider.classList.add('is-loaded');

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // A copy of the last card sits in front and a copy of the first sits behind,
    // so a step off either end lands on a clone; once the glide finishes we jump
    // to the identical original with no animation — an invisible seam.
    var head = real[real.length - 1].cloneNode(true);
    var tail = real[0].cloneNode(true);
    head.setAttribute('aria-hidden', 'true');
    tail.setAttribute('aria-hidden', 'true');
    head.removeAttribute('data-shopify-editor-block');
    tail.removeAttribute('data-shopify-editor-block');
    track.insertBefore(head, real[0]);
    track.appendChild(tail);

    var slides = Array.prototype.slice.call(track.children);
    var index = 1; // slides[1] is the first real review
    var animId = null;

    // Card pitch only changes when the row is re-laid out, so measure it once
    // and reuse it. Reading offsetLeft on every step forced a synchronous
    // layout per frame; `invalidatePitch()` drops the cache on resize.
    var cachedPitch = 0;

    function pitch() {
      if (!cachedPitch) cachedPitch = slides[1].offsetLeft - slides[0].offsetLeft;
      return cachedPitch;
    }

    function invalidatePitch() {
      cachedPitch = 0;
    }

    function posOf(i) {
      return i * pitch();
    }

    function cancelAnim() {
      if (animId) {
        window.cancelAnimationFrame(animId);
        animId = null;
      }
    }

    function animateTo(target, done) {
      cancelAnim();
      var start = track.scrollLeft;
      var delta = target - start;
      if (!delta || reduce) {
        track.scrollLeft = target;
        if (done) done();
        return;
      }
      var t0 = null;
      var duration = 420;
      function stepFrame(ts) {
        if (t0 === null) t0 = ts;
        var p = Math.min(1, (ts - t0) / duration);
        var eased = 1 - Math.pow(1 - p, 3);
        track.scrollLeft = start + delta * eased;
        if (p < 1) {
          animId = window.requestAnimationFrame(stepFrame);
        } else {
          animId = null;
          if (done) done();
        }
      }
      animId = window.requestAnimationFrame(stepFrame);
    }

    function realIndex() {
      return (index - 1 + real.length) % real.length;
    }

    function syncDots() {
      var active = realIndex();
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === active);
        dot.setAttribute('aria-current', i === active ? 'true' : 'false');
      });
    }

    function normalize() {
      if (index === 0) {
        index = real.length;
        track.scrollLeft = posOf(index);
      } else if (index === slides.length - 1) {
        index = 1;
        track.scrollLeft = posOf(index);
      }
    }

    function moveTo(i) {
      index = Math.max(0, Math.min(slides.length - 1, i));
      syncDots();
      animateTo(posOf(index), function () {
        normalize();
        syncDots();
      });
    }

    function step(delta) {
      cancelAnim();
      normalize();
      moveTo(index + delta);
    }

    function goToReal(n) {
      cancelAnim();
      normalize();
      moveTo(n + 1);
    }

    // ---- Dots ----
    var dots = [];
    if (dotsWrap) {
      dotsWrap.innerHTML = '';
      dots = real.map(function (_, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'yc-testimonials__dot';
        dot.setAttribute('aria-label', 'Go to review ' + (i + 1));
        dot.addEventListener('click', function () {
          goToReal(i);
          bump();
        });
        dotsWrap.appendChild(dot);
        return dot;
      });
    }

    // ---- Arrows ----
    var prevBtn = slider.querySelector('[data-yct-prev]');
    var nextBtn = slider.querySelector('[data-yct-next]');
    if (prevBtn) prevBtn.addEventListener('click', function () { step(-1); bump(); });
    if (nextBtn) nextBtn.addEventListener('click', function () { step(1); bump(); });

    // ---- Drag / swipe (takes over only once clearly horizontal) ----
    var down = false, dragging = false, startX = 0, startY = 0, startScroll = 0;

    track.addEventListener('pointerdown', function (e) {
      cancelAnim();
      stopAuto();
      down = true;
      dragging = false;
      startX = e.clientX;
      startY = e.clientY;
      startScroll = track.scrollLeft;
    });

    track.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX;
      var dy = e.clientY - startY;
      if (!dragging) {
        if (Math.abs(dx) < 6 || Math.abs(dx) <= Math.abs(dy)) return;
        dragging = true;
        if (track.setPointerCapture) {
          try { track.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
        }
        track.classList.add('is-dragging');
      }
      track.scrollLeft = startScroll - dx;
    });

    function endDrag() {
      if (!down) return;
      down = false;
      track.classList.remove('is-dragging');
      if (dragging) {
        var p = pitch();
        var nearest = p ? Math.round(track.scrollLeft / p) : index;
        cancelAnim();
        moveTo(nearest);
      }
      play();
    }

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    // Swallow the click that follows a drag so nothing inside the card fires.
    track.addEventListener('click', function (e) {
      if (dragging) {
        e.preventDefault();
        e.stopPropagation();
        dragging = false;
      }
    }, true);

    // ---- Autoplay ----
    var delay = parseInt(slider.getAttribute('data-autoplay'), 10) || 5000;
    var autoId = null;

    function stopAuto() {
      if (autoId) {
        window.clearInterval(autoId);
        autoId = null;
      }
    }

    function play() {
      if (reduce) return;
      stopAuto();
      autoId = window.setInterval(function () { step(1); }, delay);
    }

    function bump() {
      if (autoId) play();
    }

    slider.addEventListener('mouseenter', stopAuto);
    slider.addEventListener('mouseleave', play);
    slider.addEventListener('focusin', stopAuto);
    slider.addEventListener('focusout', play);
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stopAuto(); else play();
    });

    function settle() {
      cancelAnim();
      track.scrollLeft = posOf(index);
      syncDots();
    }

    function resettle() {
      invalidatePitch();
      settle();
    }

    // A window resize is not the only thing that can change the card pitch — a
    // late font swap or a container change does too, and neither fires `resize`.
    // Watching the track drops the cache whenever it is actually re-laid out.
    // The callback only clears a variable, so it reads no layout of its own.
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(invalidatePitch).observe(track);
    }

    // settle() reads offsetLeft, and the clone insertions plus the rebuilt dots
    // above are all writes. Reading in the next frame instead of immediately
    // lets the browser do one layout pass rather than being forced into a
    // synchronous one mid-init.
    window.requestAnimationFrame(function () {
      settle();
      play();
    });

    window.addEventListener('load', resettle);
    window.addEventListener('resize', resettle, { passive: true });
  }

  /**
   * The band sits ~3,000px down the page. Until it is near the viewport there
   * is nothing to rotate and nothing to see, so the clone/measure/dot-build
   * work waits. Deferring is safe because the served markup is already a usable
   * scroll-snap row, and the arrows and dots stay hidden until `.is-loaded`.
   */
  function whenNearViewport(element, callback) {
    if (typeof IntersectionObserver === 'undefined') {
      callback(element);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          callback(entry.target);
        });
      },
      { rootMargin: '200px' }
    );

    observer.observe(element);
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var sliders = scope.querySelectorAll('[data-yc-testimonials]');
    Array.prototype.forEach.call(sliders, function (slider) {
      whenNearViewport(slider, initSlider);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Theme editor: re-init when this section is (re)loaded.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
